import React, { useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import Amplify from '@aws-amplify/core';

import * as realTimeApi from './real-time-api-config';
import { AppContext } from './context/app-context';
import { useQuery } from './helpers/hooks/common-hook';
import { readCookie } from './helpers/utils/cookie-util';
import { PUBLISHED_APP_PREFIX } from './consts/common-consts';
import ErrorBoundary from './components/error-boundary';
import ChatPage from './pages/chat';

// The mock transport has no Amplify config; configuring with an empty object
// would only produce warnings.
if (realTimeApi.config?.aws_appsync_graphqlEndpoint) {
  Amplify.configure(realTimeApi.config);
}

export function getPublishedApp(appId) {
  return `${appId}_${PUBLISHED_APP_PREFIX}`;
}

function MissingAppId() {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink">No agent selected</h1>
        <p className="mt-2 text-[13px] text-ink-muted">
          Open this page with an <code className="font-mono text-accent">app_id</code> query
          parameter, for example <span className="font-mono">?app_id=your-app-id</span>.
        </p>
      </div>
    </div>
  );
}

export default function App() {
  const query = useQuery();

  const appInfo = useMemo(() => {
    const originalAppId = query.get('app_id');
    // Published agents are addressed as `<appId>_live`; `test_mode=1` targets
    // the draft instead. Same contract as the web-plugin.
    const appId =
      originalAppId && query.get('test_mode') !== '1'
        ? getPublishedApp(originalAppId)
        : originalAppId;

    return {
      appId,
      originalAppId,
      accessKey: query.get('access_key') || readCookie('access-key'),
    };
  }, [query]);

  return (
    <ErrorBoundary>
      <AppContext.Provider value={{ appInfo }}>
        <Routes>
          <Route path="*" element={appInfo.appId ? <ChatPage /> : <MissingAppId />} />
        </Routes>
      </AppContext.Provider>
    </ErrorBoundary>
  );
}
